/**
 * Google Analytics 4 loader + consent-gated SPA page tracking.
 *
 * Set REACT_APP_GA_MEASUREMENT_ID in your .env (e.g. G-XXXXXXXXXX).
 * Without it, this component is a no-op — no script is loaded.
 *
 * Consent flow:
 *  - public/index.html sets Consent Mode v2 defaults to "denied" before any script runs.
 *  - On mount we load gtag.js and, if the user previously accepted the cookie banner,
 *    immediately call gtag('consent', 'update', granted).
 *  - App.js wires CookieConsent's onAccept / onDecline to update consent live.
 *
 * Page-view tracking:
 *  - We configure gtag with send_page_view: false so we control SPA navigation events.
 *  - On every route change we fire a manual 'page_view' event. Consent Mode handles
 *    whether the hit actually transmits.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getCookieConsentValue } from "react-cookie-consent";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const COOKIE_NAME = "bbs_cookie_consent";

let scriptInjected = false;

const injectGaScript = (id) => {
  if (scriptInjected || !id || typeof document === "undefined") return;
  scriptInjected = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };
  window.gtag("js", new Date());
  window.gtag("config", id, { send_page_view: false });
};

const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    if (!GA_ID) return;
    injectGaScript(GA_ID);

    // Restore consent state across page reloads after the user has accepted.
    if (getCookieConsentValue(COOKIE_NAME) === "true" && window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
      });
    }
  }, []);

  useEffect(() => {
    if (!GA_ID || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  return null;
};

export default Analytics;
